import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';

import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import localStorage from 'LocalStorage';

const STROKE_WIDTH = 2;
const HALF_STROKE = STROKE_WIDTH / 2;
const TRACE_DURATION_MS = 1500;
const FADE_DURATION_MS = 3000;
const MAX_PLAY_COUNT = 3;
const STORAGE_KEY = 'llm-entity-ask-btn-animation-count';

function computePerimeter(width: number, height: number, rx: number): number {
  const rw = width - STROKE_WIDTH;
  const rh = height - STROKE_WIDTH;
  return 2 * (rw + rh) - 8 * rx + 2 * Math.PI * rx;
}

let nextGradientId = 0;

function getPlayCount(): number {
  const raw = localStorage.json.getItem(STORAGE_KEY);
  return typeof raw === 'number' && !Number.isNaN(raw) ? raw : 0;
}

export default function EntityAskBorderAnimation({children}: {children: React.ReactNode}): JSX.Element {
  const {llmAnimateAskEntityBtn} = useRolloutFlags();

  if (!llmAnimateAskEntityBtn || getPlayCount() >= MAX_PLAY_COUNT) {
    return <>{children}</>;
  }

  return <EntityAskBorderAnimationInner>{children}</EntityAskBorderAnimationInner>;
}

type Phase = 'tracing' | 'fading' | 'done';

function EntityAskBorderAnimationInner({children}: {children: React.ReactNode}): JSX.Element {
  const gradientId = useRef(`entity-ask-border-grad-${nextGradientId++}`).current;
  const containerRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<SVGRectElement>(null);
  const rafRef = useRef<number>(0);
  const [phase, setPhase] = useState<Phase>('tracing');
  const [measurements, setMeasurements] = useState<{
    width: number;
    height: number;
    borderRadius: number;
  } | null>(null);

  // Defer measurement by one frame so dynamically injected styles (aphrodite) settle
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (!containerRef.current) {
        return;
      }
      const child = containerRef.current.firstElementChild;
      if (!child) {
        return;
      }

      const rect = child.getBoundingClientRect();
      const computedRadius = parseFloat(getComputedStyle(child).borderRadius) || 4;

      if (rect.width <= STROKE_WIDTH || rect.height <= STROKE_WIDTH) {
        return;
      }

      setMeasurements({
        width: rect.width,
        height: rect.height,
        borderRadius: Math.max(computedRadius - HALF_STROKE, 0),
      });

      // Increment play count (best effort)
      localStorage.json.setItem(STORAGE_KEY, getPlayCount() + 1);
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    if (phase !== 'tracing' || !measurements) {
      return;
    }

    const {width, height, borderRadius} = measurements;
    const perimeter = computePerimeter(width, height, borderRadius);
    let startTime: number | null = null;

    const animate = (timestamp: number): void => {
      if (startTime == null) {
        startTime = timestamp;
      }
      const elapsed = timestamp - startTime;
      // ease-out: fast start, slow finish
      const linear = Math.min(elapsed / TRACE_DURATION_MS, 1);
      const eased = 1 - (1 - linear) * (1 - linear);
      const visibleLength = eased * perimeter;

      if (rectRef.current) {
        rectRef.current.setAttribute('stroke-dasharray', `${visibleLength} ${perimeter - visibleLength}`);
      }

      if (linear < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setPhase('fading');
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [phase, measurements]);

  const perimeter =
    measurements != null
      ? computePerimeter(measurements.width, measurements.height, measurements.borderRadius)
      : 0;

  return (
    <div ref={containerRef} className={css(styles.container)}>
      {children}
      {measurements && (
        <svg className={css(styles.svg)}>
          <defs>
            <linearGradient
              id={gradientId}
              gradientUnits="userSpaceOnUse"
              x1="0"
              y1="0"
              x2={measurements.width}
              y2="0"
            >
              <stop offset="0%" stopColor="#a78bfa" />
              <stop offset="100%" stopColor="#7c3aed" />
            </linearGradient>
          </defs>
          <rect
            ref={rectRef}
            x={HALF_STROKE}
            y={HALF_STROKE}
            width={measurements.width - STROKE_WIDTH}
            height={measurements.height - STROKE_WIDTH}
            rx={measurements.borderRadius}
            ry={measurements.borderRadius}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeDasharray={`0 ${perimeter}`}
            className={phase === 'fading' ? css(styles.fadeOut) : undefined}
            style={phase === 'done' ? {opacity: 0} : undefined}
            onAnimationEnd={() => {
              setPhase('done');
            }}
          />
        </svg>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    display: 'inline-flex',
  },
  svg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
  },
  fadeOut: {
    animationName: {
      '0%': {opacity: 1},
      '100%': {opacity: 0},
    },
    animationDuration: `${FADE_DURATION_MS}ms`,
    animationTimingFunction: 'ease-out',
    animationFillMode: 'forwards',
  },
});
